import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Card } from 'react-bootstrap-5';
import { faAngleRight, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  createVaultPromises, GATE_DRAG_OPTIONS, GATE_ROLES, GATE_TYPES,
  handleNewAccessLevels,
  setupInitialCaSettings,
  setupInitialGates,
  vaultRoleKey
} from './helper';
import RoleAccessSettings, { GATE_PROPS } from './role_access_settings';
import Snackbar from 'shared/snackbar';
import ConfidentialityAgreementForm, { CA_DATA_SHAPE } from './confidentiality_agreement_form';
import { Alert } from 'react-bootstrap';
import SaveSettingsButton from './save_settings_button';
import withDragDropContext from 'helpers/react_dnd/with_drag_drop_context';

function DocumentAccessSettings({ allowDigitalSignPdfCas, bulkModifyPath, hasLeads, vaults }) {
  const [savedGates, setSavedGates] = useState(setupInitialGates(vaults));
  const [gates, setGates] = useState(setupInitialGates(vaults));
  const [caSettings, setCaSettings] = useState(setupInitialCaSettings(vaults));
  const [isSaving, setIsSaving] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errors, setErrors] = useState([]);

  function handleCaFileUploaded(uploadData, caDataKey) {
    setCaSettings(caData => ({
      ...caData,
      [caDataKey]: {
        ...caData[caDataKey],
        ...uploadData
      }
    }));
  }

  function handleSimpleCaSettingsChange(field, newValue, caDataKey) {
    setCaSettings(caData => ({
      ...caData,
      [caDataKey]: {
        ...caData[caDataKey],
        [field]: newValue
      }
    }));
  }

  function saveAccessSettings() {
    setIsSaving(true);
    setErrors([]);

    Promise.allSettled(createVaultPromises(vaults, gates, caSettings, bulkModifyPath))
      .then((results) => {
        const failureReasons = results.reduce((accumulator, result) => {
          if (result.status === 'rejected') accumulator.push(...result.reason);
          return accumulator;
        }, []);

        setErrors(failureReasons);
        if (failureReasons.length === 0) {
          setShowSnackbar(true);
          setSavedGates(gates);
          const updatedVaultData = results.find(result => result.value?.hasOwnProperty('vaultData'));
          if (updatedVaultData) dispatchVaultData(updatedVaultData);
        }
      }).finally(() => setIsSaving(false));
  }

  function dispatchVaultData(updatedVaultData) {
    const event = new CustomEvent('updateVaultData', { detail: updatedVaultData.value.vaultData });
    document.dispatchEvent(event);
  }

  function handleGatesChange(modifiedGates, vaultId, role) {
    const gateKey = vaultRoleKey(vaultId, role);
    setGates({ ...gates, [gateKey]: modifiedGates });
  }

  useEffect(() => {
    document.addEventListener('accessLevelsChanged', ({ detail: { accessLevels, vaultId } }) => {
      setGates(prevGates => handleNewAccessLevels(prevGates, accessLevels, vaultId));
    });
  }, []);

  return (
    <React.Fragment>
      <Snackbar
        show={showSnackbar}
        text="Saved Access Settings"
        variant="success"
        onClose={() => setShowSnackbar(false)}
      />

      <SaveSettingsButton
        gates={gates}
        hasLeads={hasLeads}
        isSaving={isSaving}
        savedGates={savedGates}
        onClick={saveAccessSettings}
      />

      {errors.length > 0 && (
        <Alert className="mt-4 alert-danger">
          <div className="d-flex">
            <FontAwesomeIcon icon={faExclamationCircle} size="2x"/>
            <div className="ml-4 mt-1">
              <h4>The following errors occurred when trying to save this form. Please fix and re-submit.</h4>
              <ul className="mb-0">
                {errors.map(error => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </Alert>
      )}

      <Accordion alwaysOpen defaultActiveKey={vaults.map(v => v.id)}>
        {vaults.map(vault => (
          <Accordion.Item className="doc-center-vault-settings-accordion mt-5" eventKey={vault.id} key={vault.id}>
            <Card>
              <Accordion.Button className={`card-header btn w-100 btn-link ${vaults.length === 1 ? 'd-none' : ''}`}>
                <div className="accordion-head-text">
                  <span className="text-capitalize">{vault.saleOrLease}</span> Access
                </div>
                <FontAwesomeIcon className="rotate-icon" icon={faAngleRight}/>
              </Accordion.Button>
              <Accordion.Body>
                {GATE_ROLES.map((role) => {
                  const keyForRole = vaultRoleKey(vault.id, role.type);
                  const gatesForRole = gates[keyForRole];
                  const showCa = gatesForRole[GATE_TYPES.IN_PROGRESS]
                    .some(gate => gate.gateType === GATE_DRAG_OPTIONS.SIGN_CA);
                  const simpleFieldChange = (field, newValue) => {
                    handleSimpleCaSettingsChange(field, newValue, keyForRole);
                  };
                  const caFileUploaded = uploadData => handleCaFileUploaded(uploadData, keyForRole);
                  return (
                    <div
                      className="panel panel-default soft-panel p-4 mt-5 d-flex flex-column gap-4"
                      key={role.type}
                    >
                      <RoleAccessSettings
                        caOptions={vault.caOptions}
                        gates={gatesForRole}
                        role={role}
                        vaultId={vault.id}
                        onGatesChange={handleGatesChange}
                      />
                      <ConfidentialityAgreementForm
                        allowDigitalSignPdfCas={allowDigitalSignPdfCas}
                        caData={caSettings[keyForRole]}
                        caOptions={vault.caOptions}
                        caUrl={`/vaults/${vault.id}/ca/${role.type}`}
                        roleLabel={role.label}
                        show={showCa}
                        standardText={role.type === 'broker' ? vault.standardBrokerCa : vault.standardOwnerCa}
                        onCaUpload={caFileUploaded}
                        onSimpleFieldChange={simpleFieldChange}
                      />
                    </div>
                  );
                })}
              </Accordion.Body>
            </Card>
          </Accordion.Item>
        ))}
      </Accordion>
    </React.Fragment>
  );
}

DocumentAccessSettings.propTypes = {
  allowDigitalSignPdfCas: PropTypes.bool.isRequired,
  bulkModifyPath: PropTypes.string.isRequired,
  hasLeads: PropTypes.bool.isRequired,
  vaults: PropTypes.arrayOf(PropTypes.shape({
    brokerCaData: PropTypes.shape(CA_DATA_SHAPE),
    caOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
    gates: GATE_PROPS,
    id: PropTypes.number.isRequired,
    ownerCaData: PropTypes.shape(CA_DATA_SHAPE),
    saleOrLease: PropTypes.oneOf(['sale', 'lease']),
    standardBrokerCa: PropTypes.string.isRequired,
    standardOwnerCa: PropTypes.string.isRequired,
    startingAccessLevels: PropTypes.arrayOf(PropTypes.string).isRequired
  })),
};

export default withDragDropContext(DocumentAccessSettings);
